import {
  createItemRequest,
  gToLb,
  lbToG,
  mmToIn,
  inToMm,
  STORAGE_CLASSES,
  UOM_LABEL,
  type ItemDto,
  type ItemUomInput,
  type UomCode,
} from '@zenarty/shared';
import { Download, Package, Pencil, Plus, Power, Upload } from 'lucide-react';
import { useState } from 'react';
import { api, downloadFile } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import { ImportDialog } from '../components/importer';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Field,
  KeyValue,
  PageHeader,
  StatusBadge,
  errorText,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { useToast } from '../components/toast';
import { formatDateTime, titleCase } from '../lib/format';
import { nameMap, useAction, useClients, useServerList } from '../lib/queries';

const TEMP_TONE = { ambient: 'neutral', chilled: 'info', frozen: 'accent' } as const;

export function ControlBadges({ item }: { item: ItemDto }) {
  return (
    <div className="row" style={{ gap: 4 }}>
      {item.lotControlled ? <Badge>Lot</Badge> : null}
      {item.expiryControlled ? <Badge tone="warn">Expiry</Badge> : null}
      {item.serialControlled ? <Badge tone="info">Serial</Badge> : null}
      {item.catchWeight ? <Badge>Catch weight</Badge> : null}
      {item.fsmaTraceable ? <Badge tone="ok">FSMA 204</Badge> : null}
      {item.unNumber ? <Badge tone="danger">{item.unNumber}</Badge> : null}
      {!item.lotControlled && !item.expiryControlled && !item.serialControlled && !item.unNumber ? (
        <span className="faint">—</span>
      ) : null}
    </div>
  );
}

const packSummary = (i: ItemDto) =>
  i.uoms
    .filter((u) => u.uom !== i.baseUom)
    .map((u) => `${u.uom} ${u.qtyPerBase.toLocaleString()}`)
    .join(' · ') || 'Each only';

const dims = (u: ItemUomInput) =>
  u.lengthMm && u.widthMm && u.heightMm
    ? `${mmToIn(u.lengthMm).toFixed(1)} × ${mmToIn(u.widthMm).toFixed(1)} × ${mmToIn(u.heightMm).toFixed(1)} in`
    : '—';

export function ItemsPage() {
  const { can } = useAuth();
  const clients = useClients();
  const [clientId, setClientId] = useState('');
  const [temp, setTemp] = useState('');
  const [status, setStatus] = useState('active');
  const { query, server } = useServerList<ItemDto>('items', '/items', {
    clientId,
    temperatureClass: temp,
    status,
  });
  const [editing, setEditing] = useState<ItemDto | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const toast = useToast();
  const clientName = nameMap(clients.data?.items, (c) => c.name);
  const single = (clients.data?.items.length ?? 0) === 1;

  const columns: Column<ItemDto>[] = [
    {
      id: 'sku',
      header: 'Item',
      cell: (i) => (
        <div style={{ minWidth: 220 }}>
          <div className="primary-cell">{i.description}</div>
          <div className="sub">
            <span className="mono">{i.sku}</span>
            {i.gtin ? <span className="faint"> · GTIN {i.gtin}</span> : null}
          </div>
        </div>
      ),
    },
    ...(single
      ? []
      : [
          {
            id: 'client',
            header: 'Client',
            cell: (i: ItemDto) => clientName.get(i.clientId) ?? '—',
          },
        ]),
    {
      id: 'pack',
      header: 'Packs',
      cell: (i) => <span className="muted nowrap">{packSummary(i)}</span>,
    },
    {
      id: 'temp',
      header: 'Storage',
      cell: (i) => (
        <Badge tone={TEMP_TONE[i.temperatureClass]}>{titleCase(i.temperatureClass)}</Badge>
      ),
    },
    { id: 'rotation', header: 'Rotation', cell: (i) => <span className="tag">{i.rotation}</span> },
    { id: 'controls', header: 'Controls', cell: (i) => <ControlBadges item={i} /> },
    { id: 'status', header: 'Status', cell: (i) => <StatusBadge status={i.status} /> },
  ];

  const open = query.data?.items.find((i) => i.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Items"
        description="Every SKU you store, owned by one client, with pack levels, dimensions and the controls that drive receiving, rotation and shipping."
        actions={
          <>
            <Button
              icon={Download}
              onClick={() =>
                void downloadFile(
                  `/items/export${clientId ? `?clientId=${clientId}` : ''}`,
                  'items.csv',
                ).catch((e: unknown) => toast.error(errorText(e)))
              }
            >
              Export
            </Button>
            {can('item:manage') ? (
              <>
                <Button icon={Upload} onClick={() => setImporting(true)}>
                  Import
                </Button>
                <Button variant="primary" icon={Plus} onClick={() => setEditing('new')}>
                  Add item
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(i) => i.id}
        loading={query.isLoading}
        searchPlaceholder="Search SKU, description or GTIN"
        server={server}
        onRowClick={(i) => setOpenId(i.id)}
        toolbar={
          <>
            {!single ? (
              <select
                className="select"
                aria-label="Client"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                <option value="">All clients</option>
                {(clients.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : null}
            <select
              className="select"
              aria-label="Storage"
              value={temp}
              onChange={(e) => setTemp(e.target.value)}
            >
              <option value="">Any storage</option>
              <option value="ambient">Ambient</option>
              <option value="chilled">Chilled</option>
              <option value="frozen">Frozen</option>
            </select>
            <select
              className="select"
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="">All</option>
            </select>
          </>
        }
        empty={
          <EmptyState
            icon={Package}
            title="No items match"
            action={
              can('item:manage') ? (
                <Button icon={Upload} onClick={() => setImporting(true)}>
                  Import from CSV
                </Button>
              ) : null
            }
          >
            Add items one by one or import a client's whole catalogue from CSV.
          </EmptyState>
        }
      />
      {open ? (
        <ItemDrawer
          item={open}
          clientName={clientName.get(open.clientId)}
          onEdit={() => setEditing(open)}
          onClose={() => setOpenId(null)}
        />
      ) : null}
      {editing ? (
        <ItemDialog
          item={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {importing ? <ItemImport onClose={() => setImporting(false)} /> : null}
    </>
  );
}

function ItemDrawer({
  item,
  clientName,
  onEdit,
  onClose,
}: {
  item: ItemDto;
  clientName?: string;
  onEdit: () => void;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const toggle = useAction(
    () =>
      api(`/items/${item.id}`, {
        method: 'PATCH',
        body: { status: item.status === 'active' ? 'inactive' : 'active' },
      }),
    {
      invalidate: [['items']],
      success: item.status === 'active' ? 'Item deactivated' : 'Item activated',
      onSuccess: () => setConfirm(false),
    },
  );
  return (
    <Drawer
      title={item.description}
      subtitle={
        <>
          <span className="mono">{item.sku}</span>
          {clientName ? ` · ${clientName}` : ''}
        </>
      }
      icon={
        <span className="icon-tile accent">
          <Package />
        </span>
      }
      onClose={onClose}
      footer={
        can('item:manage') ? (
          <>
            <Button icon={Pencil} onClick={onEdit}>
              Edit
            </Button>
            <Button
              variant={item.status === 'active' ? 'danger' : 'default'}
              icon={Power}
              onClick={() => setConfirm(true)}
            >
              {item.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        ) : null
      }
    >
      <section>
        <div className="section-title">Identification</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={item.status} />],
            ['GTIN', item.gtin ? <code key="g">{item.gtin}</code> : null],
            ['Storage', `${titleCase(item.temperatureClass)} · ${titleCase(item.storageClass)}`],
            [
              'Hazmat',
              item.unNumber ? `Class ${item.hazmatClass ?? '?'} · ${item.unNumber}` : 'No',
            ],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Controls</div>
        <KeyValue
          items={[
            ['Tracked by', <ControlBadges key="c" item={item} />],
            ['Rotation', item.rotation],
            ['Shelf life', item.shelfLifeDays ? `${item.shelfLifeDays} days` : null],
            [
              'Min. life to ship',
              item.minShipShelfLifeDays ? `${item.minShipShelfLifeDays} days remaining` : null,
            ],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Pack levels</div>
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Unit</th>
                <th className="right">Eaches</th>
                <th>Dimensions</th>
                <th className="right">Weight</th>
              </tr>
            </thead>
            <tbody>
              {item.uoms.map((u) => (
                <tr key={u.uom}>
                  <td>
                    <strong>{UOM_LABEL[u.uom] ?? u.uom}</strong>
                    {u.ti && u.hi ? (
                      <div className="sub">
                        TI-HI {u.ti} × {u.hi}
                      </div>
                    ) : null}
                    {u.gtin ? <div className="sub mono">{u.gtin}</div> : null}
                  </td>
                  <td className="right num">{u.qtyPerBase.toLocaleString()}</td>
                  <td className="muted nowrap">{dims(u)}</td>
                  <td className="right num nowrap">
                    {u.weightG ? `${gToLb(u.weightG).toFixed(2)} lb` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section>
        <div className="section-title">Record</div>
        <KeyValue
          items={[
            ['Created', formatDateTime(item.createdAt)],
            ['Last changed', formatDateTime(item.updatedAt)],
          ]}
        />
      </section>
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={item.id} />
        </section>
      ) : null}
      {confirm ? (
        <ConfirmDialog
          title={`${item.status === 'active' ? 'Deactivate' : 'Activate'} ${item.sku}?`}
          confirmLabel={item.status === 'active' ? 'Deactivate' : 'Activate'}
          danger={item.status === 'active'}
          pending={toggle.isPending}
          onConfirm={() => toggle.mutate(undefined)}
          onClose={() => setConfirm(false)}
        >
          {item.status === 'active'
            ? 'It can no longer be received or ordered. Stock already on hand is not affected.'
            : 'It can be received and ordered again.'}
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

interface PackRow {
  uom: UomCode;
  on: boolean;
  qty: string;
  gtin: string;
  l: string;
  w: string;
  h: string;
  lb: string;
  ti: string;
  hi: string;
}

const LEVELS: UomCode[] = ['EA', 'IP', 'CS', 'PL'];
const num = (v: string) => (v.trim() === '' ? null : Number(v));

function toPackRows(item?: ItemDto): PackRow[] {
  return LEVELS.map((uom) => {
    const u = item?.uoms.find((x) => x.uom === uom);
    return {
      uom,
      on: uom === 'EA' || Boolean(u),
      qty: u ? String(u.qtyPerBase) : uom === 'EA' ? '1' : '',
      gtin: u?.gtin ?? '',
      l: u?.lengthMm ? mmToIn(u.lengthMm).toFixed(2) : '',
      w: u?.widthMm ? mmToIn(u.widthMm).toFixed(2) : '',
      h: u?.heightMm ? mmToIn(u.heightMm).toFixed(2) : '',
      lb: u?.weightG ? gToLb(u.weightG).toFixed(3) : '',
      ti: u?.ti ? String(u.ti) : '',
      hi: u?.hi ? String(u.hi) : '',
    };
  });
}

function ItemDialog({ item, onClose }: { item?: ItemDto; onClose: () => void }) {
  const clients = useClients();
  const [f, setF] = useState({
    clientId: item?.clientId ?? '',
    sku: item?.sku ?? '',
    description: item?.description ?? '',
    gtin: item?.gtin ?? '',
    temperatureClass: item?.temperatureClass ?? 'ambient',
    storageClass: item?.storageClass ?? 'standard',
    hazmatClass: item?.hazmatClass ?? '',
    unNumber: item?.unNumber ?? '',
    lotControlled: item?.lotControlled ?? false,
    serialControlled: item?.serialControlled ?? false,
    expiryControlled: item?.expiryControlled ?? false,
    catchWeight: item?.catchWeight ?? false,
    fsmaTraceable: item?.fsmaTraceable ?? false,
    rotation: item?.rotation ?? 'FIFO',
    shelfLifeDays: item?.shelfLifeDays ? String(item.shelfLifeDays) : '',
    minShipShelfLifeDays: item?.minShipShelfLifeDays ? String(item.minShipShelfLifeDays) : '',
  });
  const [packs, setPacks] = useState<PackRow[]>(() => toPackRows(item));
  const [problems, setProblems] = useState<string[]>([]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const setPack = (i: number, patch: Partial<PackRow>) =>
    setPacks((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = useAction(
    (body: unknown) =>
      item
        ? api<ItemDto>(`/items/${item.id}`, { method: 'PATCH', body })
        : api<ItemDto>('/items', { method: 'POST', body }),
    { invalidate: [['items']], success: item ? 'Item updated' : 'Item added', onSuccess: onClose },
  );

  const submit = () => {
    const uoms = packs
      .filter((p) => p.on)
      .map((p) => ({
        uom: p.uom,
        qtyPerBase: Number(p.qty),
        gtin: p.gtin.trim() || null,
        lengthMm: num(p.l) === null ? null : inToMm(Number(p.l)),
        widthMm: num(p.w) === null ? null : inToMm(Number(p.w)),
        heightMm: num(p.h) === null ? null : inToMm(Number(p.h)),
        weightG: num(p.lb) === null ? null : lbToG(Number(p.lb)),
        ti: num(p.ti),
        hi: num(p.hi),
        isPickUom: p.uom === 'EA',
      }));
    const body = {
      ...f,
      gtin: f.gtin.trim() || null,
      hazmatClass: f.hazmatClass.trim() || null,
      unNumber: f.unNumber.trim().toUpperCase() || null,
      shelfLifeDays: num(f.shelfLifeDays),
      minShipShelfLifeDays: num(f.minShipShelfLifeDays),
      baseUom: 'EA',
      uoms,
    };
    const parsed = createItemRequest.safeParse(
      item ? { ...body, clientId: item.clientId, sku: item.sku } : body,
    );
    if (!parsed.success) {
      setProblems(parsed.error.issues.map((i) => `${i.path.join('.') || 'item'}: ${i.message}`));
      return;
    }
    setProblems([]);
    if (item) {
      const { clientId: _c, sku: _s, ...patch } = parsed.data;
      save.mutate(patch);
    } else save.mutate(parsed.data);
  };

  return (
    <Dialog
      title={item ? `Edit ${item.sku}` : 'Add an item'}
      description="Dimensions in inches and weights in pounds; they are stored in millimetres and grams."
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {item ? 'Save changes' : 'Add item'}
          </Button>
        </>
      }
    >
      {problems.length ? (
        <Callout tone="danger" title="Check these fields">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      <div className="form-grid">
        {!item ? (
          <Field label="Client">
            <select
              className="select"
              value={f.clientId}
              onChange={(e) => set('clientId', e.target.value)}
            >
              <option value="">Choose the owner…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {!item ? (
          <Field label="SKU" hint="The client's item number">
            <input
              className="input mono"
              value={f.sku}
              onChange={(e) => set('sku', e.target.value)}
            />
          </Field>
        ) : null}
        <Field label="Description" className="span-2">
          <input
            className="input"
            value={f.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </Field>
        <Field label="GTIN / UPC" hint="8, 12, 13 or 14 digits; the check digit is verified">
          <input
            className="input mono"
            inputMode="numeric"
            value={f.gtin}
            onChange={(e) => set('gtin', e.target.value)}
          />
        </Field>
        <Field label="Storage temperature">
          <select
            className="select"
            value={f.temperatureClass}
            onChange={(e) => set('temperatureClass', e.target.value as typeof f.temperatureClass)}
          >
            <option value="ambient">Ambient</option>
            <option value="chilled">Chilled</option>
            <option value="frozen">Frozen</option>
          </select>
        </Field>
        <Field label="Storage class">
          <select
            className="select"
            value={f.storageClass}
            onChange={(e) => set('storageClass', e.target.value)}
          >
            {STORAGE_CLASSES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-grid" style={{ gap: 12 }}>
          <Field label="Hazmat class">
            <input
              className="input"
              placeholder="e.g. 2.1"
              value={f.hazmatClass}
              onChange={(e) => set('hazmatClass', e.target.value)}
            />
          </Field>
          <Field label="UN number">
            <input
              className="input mono"
              placeholder="UN1950"
              value={f.unNumber}
              onChange={(e) => set('unNumber', e.target.value)}
            />
          </Field>
        </div>
      </div>
      <div>
        <div className="fieldset-title">Controls</div>
        <div className="row" style={{ gap: 18 }}>
          {(
            [
              ['lotControlled', 'Lot'],
              ['expiryControlled', 'Expiry'],
              ['serialControlled', 'Serial numbers'],
              ['catchWeight', 'Catch weight'],
              ['fsmaTraceable', 'FSMA 204 traceability'],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} />{' '}
              {label}
            </label>
          ))}
        </div>
      </div>
      <div className="form-grid-3">
        <Field label="Rotation" hint="FEFO needs expiry">
          <select
            className="select"
            value={f.rotation}
            onChange={(e) => set('rotation', e.target.value as typeof f.rotation)}
          >
            <option value="FIFO">FIFO — first in, first out</option>
            <option value="FEFO">FEFO — first expiry, first out</option>
            <option value="LIFO">LIFO — last in, first out</option>
          </select>
        </Field>
        <Field label="Shelf life (days)">
          <input
            className="input"
            inputMode="numeric"
            value={f.shelfLifeDays}
            onChange={(e) => set('shelfLifeDays', e.target.value)}
          />
        </Field>
        <Field label="Min. days left to ship">
          <input
            className="input"
            inputMode="numeric"
            value={f.minShipShelfLifeDays}
            onChange={(e) => set('minShipShelfLifeDays', e.target.value)}
          />
        </Field>
      </div>
      <div>
        <div className="fieldset-title">Pack levels</div>
        <div className="card table-card">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Unit</th>
                  <th>Eaches</th>
                  <th>GTIN</th>
                  <th>L × W × H (in)</th>
                  <th>Weight (lb)</th>
                  <th>TI × HI</th>
                </tr>
              </thead>
              <tbody>
                {packs.map((p, i) => (
                  <tr key={p.uom}>
                    <td>
                      <label className="check nowrap">
                        <input
                          type="checkbox"
                          checked={p.on}
                          disabled={p.uom === 'EA'}
                          onChange={(e) => setPack(i, { on: e.target.checked })}
                        />
                        {UOM_LABEL[p.uom]}
                      </label>
                    </td>
                    <td>
                      <input
                        className="input"
                        style={{ width: 80 }}
                        disabled={!p.on || p.uom === 'EA'}
                        value={p.qty}
                        onChange={(e) => setPack(i, { qty: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input mono"
                        style={{ width: 150 }}
                        disabled={!p.on}
                        value={p.gtin}
                        onChange={(e) => setPack(i, { gtin: e.target.value })}
                      />
                    </td>
                    <td>
                      <div className="row" style={{ flexWrap: 'nowrap', gap: 4 }}>
                        {(['l', 'w', 'h'] as const).map((k) => (
                          <input
                            key={k}
                            className="input"
                            style={{ width: 62 }}
                            disabled={!p.on}
                            value={p[k]}
                            onChange={(e) => setPack(i, { [k]: e.target.value })}
                          />
                        ))}
                      </div>
                    </td>
                    <td>
                      <input
                        className="input"
                        style={{ width: 80 }}
                        disabled={!p.on}
                        value={p.lb}
                        onChange={(e) => setPack(i, { lb: e.target.value })}
                      />
                    </td>
                    <td>
                      {p.uom === 'PL' ? (
                        <div className="row" style={{ flexWrap: 'nowrap', gap: 4 }}>
                          <input
                            className="input"
                            style={{ width: 52 }}
                            disabled={!p.on}
                            value={p.ti}
                            onChange={(e) => setPack(i, { ti: e.target.value })}
                          />
                          <input
                            className="input"
                            style={{ width: 52 }}
                            disabled={!p.on}
                            value={p.hi}
                            onChange={(e) => setPack(i, { hi: e.target.value })}
                          />
                        </div>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function ItemImport({ onClose }: { onClose: () => void }) {
  const clients = useClients();
  const [clientId, setClientId] = useState('');
  return (
    <ImportDialog
      title="Import items"
      description="Validate first: every row is checked with the same rules as the item form. New SKUs are created and existing SKUs updated; nothing is written if any row has a problem."
      endpoint="/items/import"
      scope={{ clientId }}
      scopeReady={Boolean(clientId)}
      invalidate="items"
      templateName="items-template.csv"
      templateHeaders={[
        'sku',
        'description',
        'gtin',
        'temperature_class',
        'storage_class',
        'lot_controlled',
        'expiry_controlled',
        'serial_controlled',
        'rotation',
        'shelf_life_days',
        'min_ship_shelf_life_days',
        'fsma_traceable',
        'hazmat_class',
        'un_number',
        'each_weight_g',
        'each_length_mm',
        'each_width_mm',
        'each_height_mm',
        'case_qty',
        'case_gtin',
        'case_weight_g',
        'pallet_ti',
        'pallet_hi',
      ]}
      templateExample={[
        'YOG-32',
        'Greek Yogurt, Plain, 32 oz',
        '036000291452',
        'chilled',
        'standard',
        'Y',
        'Y',
        'N',
        'FEFO',
        45,
        21,
        'Y',
        '',
        '',
        950,
        110,
        110,
        140,
        6,
        '',
        6000,
        10,
        6,
      ]}
      onClose={onClose}
      picker={
        <Field label="Client the items belong to">
          <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Choose a client…</option>
            {(clients.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      }
    />
  );
}
